import { useParams, Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import Breadcrumb from '@/components/Breadcrumb'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, CheckCircle2, Phone, Mail, ArrowRight } from 'lucide-react'

const servicesData = {
  'roofing': {
    title: 'Roofing',
    description: 'Expert roofing services for all types of properties',
    longDescription: `We provide expert roofing services for all types of properties, from traditional homes to modern builds. Our skilled team handles everything from roof renewals and repairs to maintenance and inspections, ensuring your roof remains strong, weatherproof, and built to last.
    
    We have been re-roofing Brighton and Hove and throughout East and West Sussex for over 45 years. From small roof repairs to complete roof renewals, Hill Bros roofing provides a complete roofing service including tiling, slating, chimneys, guttering, fascias, flat roofing, and leadwork. All types of roofing tiles and slates fitted in accordance with B.S.5534 by our skilled and experienced roofers.`,
    features: [
      'Complete roof renewals',
      'Roof repairs and maintenance',
      'All types of tiles and slates',
      'Velux roof windows',
      'Chimney repairs',
      'Emergency roof repairs',
      'Full guarantees provided',
      'Free inspections and quotes'
    ],
    relatedServices: ['flat-roofing', 'leadwork', 'guttering-fascias']
  },
  'flat-roofing': {
    title: 'Flat Roofing',
    description: 'Durable flat roofing solutions for lasting protection',
    longDescription: `Our flat roofing solutions are designed for durability and performance. We install and repair flat roofs using a 3 layer felt system, offering reliable waterproofing and a clean, professional finish for extensions, garages, and commercial spaces. We will tackle any roof size!`,
    features: [
      'Felt roofing systems',
      'Flat roof repairs',
      'Complete replacements',
      'Waterproofing solutions',
      'Long-term guarantees',
      'Commercial and domestic'
    ],
    relatedServices: ['roofing', 'guttering-fascias', 'leadwork']
  },
  'general-building': {
    title: 'General Building',
    description: 'Comprehensive building services from start to finish',
    longDescription: `We undertake a wide range of general building services, from structural alterations and extensions to property refurbishments. Our experienced team manages projects from start to finish with precision and attention to detail.
    
    Our building division covers all aspects including brickwork, blockwork, structural alterations, extensions, renovations, and complete refurbishments. We work closely with architects and structural engineers to ensure all work meets building regulations.`,
    features: [
      'Extensions and alterations',
      'Property refurbishments',
      'Structural work',
      'Brickwork and blockwork',
      'Kitchen installations',
      'Bathroom renovations',
      'Project management',
      'Building regulations compliance'
    ],
    relatedServices: ['plastering', 'decorating', 'repointing']
  },
  'guttering-fascias': {
    title: 'Guttering & Fascias',
    description: 'Protect your home from water damage',
    longDescription: `We supply and install durable guttering and fascia systems to protect your home from water damage. Whether it's a repair, replacement, or routine maintenance, we ensure your rainwater system works efficiently all year round.`,
    features: [
      'UPVC guttering installation',
      'Cast iron gutter repairs',
      'Fascia board replacement',
      'Soffit installation',
      'Downpipe repairs',
      'Gutter cleaning services',
      'Maintenance contracts',
      'All colours available'
    ],
    relatedServices: ['roofing', 'flat-roofing', 'general-building']
  },
  'leadwork': {
    title: 'Leadwork',
    description: 'Traditional and modern leadwork specialists',
    longDescription: `Specialising in traditional and modern leadwork, we carry out repairs and installations to protect your roof from leaks while maintaining its character and charm. Our craftsmen ensure precision detailing for a lasting finish.`,
    features: [
      'Lead flashing installation',
      'Valley gutter repairs',
      'Chimney leadwork',
      'Cavity tray installations',
      'Parapet gutters',
      'Box gutters',
      'Lead welding',
      'Listed building specialists'
    ],
    relatedServices: ['roofing', 'flat-roofing', 'chimney-demolition']
  },
  'repointing': {
    title: 'Re-pointing',
    description: 'Restore the strength and appearance of your brickwork',
    longDescription: `Our re-pointing service restores the strength and appearance of your brickwork or stonework by replacing old, damaged mortar. This helps maintain structural integrity and improves your property's resistance to weather damage.`,
    features: [
      'Full property re-pointing',
      'Patch repairs',
      'Chimney re-pointing',
      'Garden wall repairs',
      'Colour-matched mortar',
      'Weather strike pointing',
      'Structural repairs',
      'Helibar brickwork stitching'
    ],
    relatedServices: ['lime-pointing', 'general-building', 'chimney-demolition']
  },
  'lime-pointing': {
    title: 'Lime Pointing',
    description: 'Traditional lime pointing for period properties',
    longDescription: `We offer traditional lime pointing for historic and period properties, ensuring breathability and authenticity in line with conservation best practices. This method is ideal for maintaining the integrity of older buildings.`,
    features: [
      'Hot lime mortar',
      'NHL lime mortars',
      'Conservation approved',
      'Listed building work',
      'Breathable pointing',
      'Traditional techniques',
      'Colour matching',
      'Heritage specialists'
    ],
    relatedServices: ['repointing', 'general-building', 'damp-proofing']
  },
  'render-repairs': {
    title: 'Render Repairs',
    description: 'Expert render repair and renewal services',
    longDescription: `We specialise in all aspects of rendering, from minor repairs to full property render renewals. Our render repair service is ideal for addressing cracks, blown or damaged sections, and areas affected by water ingress, helping to restore both the protection and appearance of your exterior walls.
    
    For larger projects, we offer complete render renewals, removing old, failing render and applying a fresh, durable finish that enhances weather resistance and improves the overall look of your property. Using high-quality materials and skilled workmanship, we ensure a smooth, long-lasting result that stands up to the coastal elements of Brighton & Hove.`,
    features: [
      'Crack repairs',
      'Blown render replacement',
      'Water damage repairs',
      'Complete render renewals',
      'Weather-resistant finishes',
      'Coastal-suitable materials',
      'Smooth professional finish',
      'Long-lasting results'
    ],
    relatedServices: ['repointing', 'lime-pointing', 'damp-proofing']
  },
  'damp-proofing': {
    title: 'Damp Proofing',
    description: 'Comprehensive damp proofing solutions',
    longDescription: `Our damp proofing solutions protect your property from rising damp, condensation, and penetrating damp. We diagnose and treat issues effectively, helping to preserve your home's structure and prevent long-term damage.`,
    features: [
      'Rising damp treatment',
      'Newtionite damp proof systems',
      'Tanking systems',
      'Condensation control',
      'Penetrating damp solutions',
      'Basement waterproofing',
      'Timber treatment',
      '20-year guarantees'
    ],
    relatedServices: ['plastering', 'lime-pointing', 'general-building']
  },
  'loft-insulation': {
    title: 'Loft Insulation',
    description: 'Energy-efficient loft insulation upgrades',
    longDescription: `Improve your home's energy efficiency and comfort with our professional loft insulation upgrade service. We replace outdated or insufficient insulation with high-quality materials that provide superior thermal performance, helping to keep your home warmer in winter and cooler in summer. Our team ensures every area of your loft is properly insulated and ventilated, reducing heat loss, lowering energy bills, and enhancing overall living comfort – all while meeting modern building standards.`,
    features: [
      'High-quality insulation materials',
      'Superior thermal performance',
      'Energy bill reduction',
      'Proper ventilation maintained',
      'Modern building standards',
      'Complete loft coverage',
      'Professional installation',
      'Year-round comfort improvement'
    ],
    relatedServices: ['damp-proofing', 'general-building', 'plastering']
  },
  'plastering': {
    title: 'Plastering',
    description: 'Expert plastering for perfect finishes',
    longDescription: `We provide expert plastering services for smooth, flawless walls and ceilings. From skimming and repairs to full plastering for new builds or refurbishments, our finish ensures surfaces are ready for decorating.`,
    features: [
      'Wall skimming',
      'Ceiling repairs',
      'New build plastering',
      'Dry lining',
      'Float and set',
      'Patch repairs',
      'Artex removal',
      'Decorative plasterwork'
    ],
    relatedServices: ['decorating', 'general-building', 'damp-proofing']
  },
  'decorating': {
    title: 'Decorating',
    description: 'Professional painting and decorating services',
    longDescription: `From interior makeovers to exterior finishes, we offer high-quality painting and decorating services. We also offer end-of-tenancy redecorations, ensuring properties are left looking fresh and ready for new occupants. Our finishes enhance your property's appearance while protecting it from the elements.`,
    features: [
      'Interior painting',
      'Exterior decorating',
      'Wallpaper hanging',
      'End-of-tenancy decoration',
      'Commercial decorating',
      'Spray painting',
      'Period property specialists',
      'Eco-friendly paints available'
    ],
    relatedServices: ['plastering', 'general-building', 'sash-window-repairs']
  },
  'sash-window-repairs': {
    title: 'Sash Window Repairs',
    description: 'Restore your traditional sash windows',
    longDescription: `We offer high quality repairs and restoration of traditional sash windows, maintaining the character of your property while improving functionality and energy efficiency. From minor repairs to full refurbishments, we ensure your windows operate smoothly and look their best.`,
    features: [
      'Sash cord replacement',
      'Draught proofing',
      'Window furniture repairs',
      'Rebalancing weights',
      'Timber repairs',
      'Sash window maintainance',
      'Listed building compliance',
      'Full restoration service'
    ],
    relatedServices: ['decorating', 'general-building', 'leadwork']
  },
  'drone-surveys': {
    title: 'Drone Surveys',
    description: 'Safe, detailed aerial inspections',
    longDescription: `Our drone surveys provide safe, detailed inspections of roofs and hard-to-reach areas without the need for scaffolding. We deliver high-resolution images and reports, helping identify issues quickly and cost-effectively.`,
    features: [
      'High-resolution imagery',
      'Video inspections',
      'Detailed reports',
      'No scaffolding needed',
      'Quick turnaround',
      'Insurance reports',
      'Cost-effective surveys'
    ],
    relatedServices: ['roofing', 'flat-roofing', 'chimney-demolition']
  },
  'chimney-demolition': {
    title: 'Chimney Demolition',
    description: 'Safe and efficient chimney removal',
    longDescription: `We offer safe and efficient chimney removal services, from partial dismantling to complete demolition. Our team ensures the structure is taken down carefully and the surrounding roof area is left secure and weatherproof.`,
    features: [
      'Partial chimney removal',
      'Complete demolition',
      'Structural assessments',
      'Roof making good',
      'Scaffold provision',
      'Waste removal',
      'Roof structure reinforcement',
      'Fully insured service'
    ],
    relatedServices: ['roofing', 'leadwork', 'repointing']
  }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
}

function Services() {
  const { service } = useParams()
  const navigate = useNavigate()
  
  // If no specific service, show all services
  if (!service) {
    return (
      <div className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Breadcrumb items={[{ label: 'Services' }]} />
            <h1 className="text-4xl font-bold mb-8">Our Services</h1>
            <p className="text-xl text-muted-foreground mb-12 max-w-3xl">
              With over 45 years of experience, we offer a comprehensive range of roofing 
              and building services. Each service is delivered with the same commitment to 
              quality and customer satisfaction.
            </p>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {Object.entries(servicesData).map(([key, data]) => (
              <motion.div key={key} variants={itemVariants}>
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <CardHeader>
                    <CardTitle className="text-xl">{data.title}</CardTitle>
                    <CardDescription className="text-base">{data.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      asChild 
                      className="w-full group"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(`/services/${key}`)
                      }}
                    >
                      <span className="cursor-pointer">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    )
  }

  // Show specific service
  const serviceData = servicesData[service]
  
  if (!serviceData) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="py-12"
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <p className="text-muted-foreground mb-8">The service you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/services">View All Services</Link>
          </Button>
        </div>
      </motion.div>
    )
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        key={service}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="py-12"
      >
        <div className="container mx-auto px-4">
          <Breadcrumb 
            items={[
              { label: 'Services', href: '/services' },
              { label: serviceData.title }
            ]} 
          />

          <Button 
            variant="ghost" 
            className="mb-6 hover:translate-x-[-4px] transition-transform"
            onClick={() => navigate('/services')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to All Services
          </Button>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{serviceData.title}</h1>
              <p className="text-xl text-muted-foreground mb-8">{serviceData.description}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="mb-8 shadow-lg">
                <CardContent className="pt-6">
                  <p className="whitespace-pre-line text-lg leading-relaxed">{serviceData.longDescription}</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="mb-8 shadow-lg">
                <CardHeader className="bg-accent/50">
                  <CardTitle>What We Offer</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-3">
                    {serviceData.features.map((feature, index) => (
                      <motion.div 
                        key={index} 
                        className="flex items-start gap-2"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + index * 0.05 }}
                      >
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Related Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-8"
            >
              <h3 className="text-2xl font-semibold mb-4">Related Services</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {serviceData.relatedServices.map((relatedKey) => {
                  const related = servicesData[relatedKey]
                  return (
                    <Card 
                      key={relatedKey} 
                      className="hover:shadow-lg transition-all duration-300 cursor-pointer group"
                      onClick={() => navigate(`/services/${relatedKey}`)}
                    >
                      <CardHeader>
                        <CardTitle className="text-lg group-hover:text-primary transition-colors">
                          {related.title}
                        </CardTitle>
                        <CardDescription className="text-sm">
                          {related.description}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  )
                })}
              </div>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8"
            >
              <h3 className="text-2xl font-semibold mb-4">Ready to Get Started?</h3>
              <p className="text-muted-foreground mb-6">
                Contact us today for a free, no-obligation quote
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button asChild size="lg">
                  <Link to="/contact">
                    <Mail className="mr-2 h-4 w-4" />
                    Get a Quote
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:01273123456">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Now
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default Services